import ExcelJS from "exceljs";

interface ExportExcelWithResumeOptions {
	title: string;
	subtitle?: string;
	logoBase64?: string;
	data: Record<string, any>[];
	columns: { key: string; label: string }[];
	fileName?: string;
	sheetName?: string;

	// Adicionado:
	resumoPremiacao?: {
		resumo: {
			label: string;
			totalValue: number;
			totalFormatted: string;
		}[];
		saldoAPagar: number;
		totalPagos: number;
		totalCanceladosSuspensos: number;
	};
}

export async function exportExcelStyleWithResume({
	title,
	subtitle,
	logoBase64,
	data,
	columns,
	fileName = "Relatorio_Completo.xlsx",
	sheetName = "Relatório",
	resumoPremiacao,
}: ExportExcelWithResumeOptions) {
	const workbook = new ExcelJS.Workbook();
	const sheet = workbook.addWorksheet(sheetName);
	sheet.views = [{ showGridLines: false }];

	// Adiciona logo
	if (logoBase64) {
		const imageId = workbook.addImage({ base64: logoBase64, extension: "png" });
		sheet.addImage(imageId, {
			tl: { col: 0, row: 0 },
			ext: { width: 140, height: 35 },
		});
	}

	// Título e subtítulo
	sheet.mergeCells("B1:H1");
	sheet.getCell("B1").value = title;
	sheet.getCell("B1").font = { name: "Calibri", bold: true, size: 20 };
	sheet.getCell("B1").alignment = { horizontal: "left", vertical: "middle" };

	sheet.mergeCells("B2:H2");
	sheet.getCell("B2").value = subtitle ?? "";
	sheet.getCell("B2").font = { name: "Calibri", italic: true, size: 11 };

	sheet.mergeCells("B3:H3");
	sheet.getCell("B3").value = `Total de linhas: ${data.length}`;
	sheet.getCell("B3").font = { name: "Calibri", size: 11 };

	let currentRow = 5;

	// === RESUMO IGUAL AO PDF ===
	if (resumoPremiacao) {
		const { resumo, saldoAPagar, totalPagos, totalCanceladosSuspensos } = resumoPremiacao;

		// Cabeçalho "Resumo da Premiação"
		sheet.mergeCells(`B${currentRow}:G${currentRow}`);
		sheet.getCell(`B${currentRow}`).value = "Resumo da Premiação";
		sheet.getCell(`B${currentRow}`).font = { name: "Calibri", bold: true, size: 14 };
		sheet.getCell(`B${currentRow}`).alignment = { horizontal: "center" };
		currentRow++;

		// Primeira linha → Labels
		const labelsRow = sheet.getRow(currentRow);
		labelsRow.getCell(2).value = "Premiação";
		labelsRow.getCell(3).value = "Bonificação";
		labelsRow.getCell(4).value = "Estorno";
		labelsRow.getCell(5).value = "Saldo a Pagar (líquido)";
		labelsRow.getCell(6).value = "Total Pagos";
		labelsRow.getCell(7).value = "Total Cancelados";

		labelsRow.eachCell((cell) => {
			cell.font = { name: "Calibri", bold: true, color: { argb: "FFFFFFFF" } };
			cell.fill = {
				type: "pattern",
				pattern: "solid",
				fgColor: { argb: "FF4F81BD" },
			};
			cell.alignment = { horizontal: "center" };
		});
		currentRow++;

		// Segunda linha → Valores
		const valuesRow = sheet.getRow(currentRow);
		valuesRow.getCell(2).value = resumo[0]?.totalFormatted ?? "R$ 0,00";
		valuesRow.getCell(3).value = resumo[1]?.totalFormatted ?? "R$ 0,00";
		valuesRow.getCell(4).value = resumo[2]?.totalFormatted ?? "R$ 0,00";
		valuesRow.getCell(5).value = `R$ ${saldoAPagar.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
		valuesRow.getCell(6).value = `R$ ${totalPagos.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
		valuesRow.getCell(7).value = `R$ ${totalCanceladosSuspensos.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

		valuesRow.eachCell((cell) => {
			cell.font = { name: "Calibri", bold: false };
			cell.alignment = { horizontal: "center" };
		});
		currentRow += 2;
	}
	// === FIM DO RESUMO ===

	// Extrato das Bonificações/Premiações — Centralizar o título MESCLADO A → J
	sheet.mergeCells(`A${currentRow}:J${currentRow}`);
	sheet.getCell(`A${currentRow}`).value = "Extrato das Bonificações/Premiações";
	sheet.getCell(`A${currentRow}`).font = { name: "Calibri", bold: true, size: 14 };
	sheet.getCell(`A${currentRow}`).alignment = { horizontal: "center" };
	currentRow++;

	// Cabeçalho da tabela — com columns na ordem que você passou
	const headerRow = sheet.getRow(currentRow);
	const headerValues = columns.map((col) => col.label);
	headerRow.values = headerValues;
	headerRow.font = { name: "Calibri", bold: true };
	headerRow.eachCell((cell) => {
		cell.fill = {
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: "FF4F81BD" },
		};
		cell.font = { name: "Calibri", color: { argb: "FFFFFFFF" } };
		cell.alignment = { horizontal: "center" };
	});
	currentRow++;

	// Dados da tabela
	data.forEach((item, idx) => {
		const row = sheet.getRow(currentRow + idx);

		columns.forEach((col, i) => {
			const cell = row.getCell(i + 1);

			if (col.key === "indice") {
				const numericValue = Number(item[col.key]);
				if (isNaN(numericValue)) {
					cell.value = "";
				} else {
					cell.value = numericValue / 100;
					cell.numFmt = "0.00%";
				}
				cell.alignment = { horizontal: "center" };
			} else if (["value_amount", "value"].includes(col.key)) {
				cell.value = parseFloat(item[col.key].replace("R$", "").replace(".", "").replace(",", ".")) || 0;
				cell.numFmt = "R$ #,##0.00";
				cell.alignment = { horizontal: "right" };
			} else if (["certificate", "document", "type"].includes(col.key)) {
				cell.value = item[col.key];
				cell.alignment = { horizontal: "center" };
			} else {
				cell.value = item[col.key];
				cell.alignment = { vertical: "middle", horizontal: "left" };

				if (["parcel", "due_date", "status"].includes(col.key)) {
					cell.alignment = { horizontal: "center" };

					if (col.key === "status") {
						const statusText = (item[col.key] ?? "").toLowerCase();

						if (statusText.includes("pago")) {
							cell.font = { ...cell.font, color: { argb: "FF228B22" } };
						} else if (statusText.includes("cancelado") || statusText.includes("suspenso")) {
							cell.font = { ...cell.font, color: { argb: "FFDC143C" } };
						} else {
							cell.font = { ...cell.font, color: { argb: "FF000000" } };
						}
					}
				}
			}

			cell.font = { ...cell.font, name: "Calibri" };
		});

		if (idx % 2 === 1) {
			row.eachCell((cell) => {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "FFF2F2F2" },
				};
			});
		}
	});

	// Larguras das colunas
	sheet.getColumn(1).width = 24.62;
	sheet.getColumn(2).width = 33.52;
	sheet.getColumn(3).width = 25.32;
	sheet.getColumn(4).width = 20.0;
	sheet.getColumn(5).width = 20.0;
	sheet.getColumn(6).width = 20.0;
	sheet.getColumn(7).width = 20.0;
	sheet.getColumn(8).width = 20.0;
	sheet.getColumn(9).width = 20.0;
	sheet.getColumn(10).width = 20.0;

	// Exporta
	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}
